export class VariantSelector extends HTMLElement {
  private container: HTMLDivElement
  private select: HTMLSelectElement | null
  private dropdownButton: HTMLElement | null
  private sizeLabel: HTMLElement | null
  private options: NodeListOf<HTMLElement>
  private urlSync: boolean
  private pairFormId: string | null
  private customisationFormId: string | null
  private priceSelector: string | null

  private static SELECTED_BUTTON_CLASSES = ['active']

  constructor() {
    super()
    // Create internal container element
    this.container = document.createElement('div')
    // Move all children to the container
    while (this.firstChild) {
      this.container.appendChild(this.firstChild)
    }
    // Copy attributes from custom element to container
    Array.from(this.attributes).forEach(attr => {
      if (attr.name !== 'is') {
        this.container.setAttribute(attr.name, attr.value)
      }
    })
    this.appendChild(this.container)

    this.select = null
    this.dropdownButton = null
    this.sizeLabel = null
    this.urlSync = false
    this.pairFormId = null
    this.customisationFormId = null
    this.priceSelector = null
    this.options = this.container.querySelectorAll('[data-select-option]')
  }

  connectedCallback(): void {
    // Wait for DOM to be fully loaded
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.initialize())
    } else {
      this.initialize()
    }
  }

  private initialize(): void {
    this.urlSync = this.hasAttribute('data-url-sync')
    this.pairFormId = this.getAttribute('data-pair-form-id')
    this.customisationFormId = this.getAttribute('data-customisation-form-id')
    this.priceSelector = this.getAttribute('data-price-selector')
    this.select = this.parentNode ? this.parentNode.querySelector<HTMLSelectElement>('select[name="id"]') : null
    this.dropdownButton = this.container.querySelector<HTMLElement>('[data-modal-role="toggle"]')
    this.sizeLabel = this.dropdownButton?.querySelector<HTMLElement>('[size-selector-label') || null
    this.options = this.container.querySelectorAll<HTMLElement>('[data-select-option]')

    // Set up event listeners
    this.setupEventListeners()
  }

  private updatePriceDisplay(option: HTMLElement): void {
    if (this.priceSelector) {
      const priceElement = document.getElementById(this.priceSelector)
      if (priceElement) {
        const priceCurrent = option.dataset.variantPrice || ''
        const priceCompare = option.dataset.variantPriceCompare || ''

        // Update CSS variables
        priceElement.style.setProperty('--price-current', `'${priceCurrent}'`)
        priceElement.style.setProperty('--price-compare', `'${priceCompare}'`)

        // Update data-sale attribute
        const onSale = priceCompare !== '' && priceCompare !== priceCurrent && priceCompare !== '0'
        priceElement.setAttribute('data-sale', onSale.toString())
      }
    }
  }

  private setupEventListeners(): void {
    // Handle option selection
    this.options.forEach(option => {
      option.addEventListener('click', (event) => this.handleOptionSelect(event))
    })
  }

  private updatedSelectedState(option: HTMLElement): void {
    this.options.forEach(option => {
      option.classList.remove(...VariantSelector.SELECTED_BUTTON_CLASSES)
    })

    option.classList.add(...VariantSelector.SELECTED_BUTTON_CLASSES)
  }

  private handleOptionSelect(event: Event): void {
    const option = event.currentTarget as HTMLElement
    const variantId = option.dataset.variantId || ''
    const variantOption = option.dataset.variantOption || ''

    // Update the hidden select
    if (this.select) {
      this.select.value = variantId

      // Trigger change event for any listeners
      const changeEvent = new Event('change', { bubbles: true })
      this.select.dispatchEvent(changeEvent)
    }

    // Update price display
    this.updatePriceDisplay(option)

    // Update the sticky pair form
    if (this.pairFormId) {
      const pairedElement = document.getElementById(this.pairFormId)
      if (pairedElement) {
        const hiddenSelector = pairedElement.querySelector('variant-selector')
        if (hiddenSelector) {
          const matchingOption = hiddenSelector.querySelector<HTMLElement>(`[data-variant-id="${variantId}"]`)
          if (matchingOption) {
            matchingOption.click()
          }
        }
      }
    }

    // Update the customisation form
    if (this.customisationFormId) {
      const customisationElement = document.getElementById(this.customisationFormId)
      if (customisationElement) {
        const hiddenSelector = customisationElement.querySelector('variant-selector')
        if (hiddenSelector) {
          const matchingOption = hiddenSelector.querySelector<HTMLElement>(`[data-variant-id="${variantId}"]`)
          if (matchingOption) {
            matchingOption.click()
          }
        }
      }
    }

    // Clear any other selected options
    this.updatedSelectedState(option)
    // Update the button text
    if (this.sizeLabel) {
      this.sizeLabel.textContent = variantOption

      if (this.dropdownButton) {
        this.dropdownButton.classList.add(...VariantSelector.SELECTED_BUTTON_CLASSES)
      }
    }

    if (this.urlSync) {
      // Update URL with variant
      window.history.replaceState({}, '', '?variant=' + variantId)
    }

    // Close the dropdown
    const closeButton = this.container.querySelector<HTMLElement>('[data-modal-role="close"]')
    if (closeButton) closeButton.click()
  }
}
